/**
 * Support "view as customer" — the customer-facing half.
 *
 * WHY sessionStorage. The view is held by one tab and nothing else. sessionStorage
 * is scoped to a tab, so an admin already signed in as themselves keeps that
 * session in every other tab, and closing the viewing tab ends the view with no
 * cleanup to remember and no session left open. It also means two views of two
 * different customers can be open side by side without fighting each other.
 *
 * The view is read-only — the backend refuses anything that writes — and no
 * refresh token exists for it, so after an hour it simply stops.
 */

const TOKEN_KEY = "as-view-token";
const EMAIL_KEY = "as-view-email";

/** True while this tab is showing someone else's account. */
export function isViewingAsCustomer(): boolean {
  try {
    return !!sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return false;
  }
}

/** Whose account this tab is showing, for the bar at the top. */
export function viewingEmail(): string | null {
  try {
    return sessionStorage.getItem(EMAIL_KEY);
  } catch {
    return null;
  }
}

/**
 * The access token for this tab: the view session if there is one, otherwise
 * the ordinary signed-in token. Every caller that needs a token goes through
 * here, so a viewing tab and a normal tab differ in exactly one place.
 */
export function currentAccessToken(): string | null {
  try {
    const viewToken = sessionStorage.getItem(TOKEN_KEY);
    if (viewToken) return viewToken;
  } catch {
    /* sessionStorage can be unavailable in hardened browsers */
  }
  try {
    const raw = localStorage.getItem("accessToken");
    return raw ? JSON.parse(raw).value : null;
  } catch {
    return null;
  }
}

export function beginView(token: string, email: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(EMAIL_KEY, email);
}

/** Drop the view. Touches nothing belonging to the admin's own session. */
export function endView(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Redeem the handover code for this tab's view session.
 *
 * Deliberately a bare fetch rather than the app's axios instance: this runs
 * before the store exists, and the instance's interceptor would attach whatever
 * token is already in localStorage — the admin's own, which has nothing to do
 * with the account being opened.
 */
export async function redeemViewCode(code: string): Promise<void> {
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/view-as/exchange`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) return;
    const data = await res.json();
    if (data?.accessToken) beginView(data.accessToken, data.viewingEmail ?? "");
  } catch {
    /* A dead or spent code just means no view — the site loads as a guest. */
  }
}

/**
 * Guard for controls that update themselves before the server answers.
 *
 * A support view is refused server-side whatever the screen does, but an
 * optimistic control still flips — so a favourite would show as set when
 * nothing was saved. Anything that changes its own appearance first has to ask
 * this before it does, and say why instead.
 *
 * The toast id matches the one the request layer uses, so a control that asks
 * here and a control that simply lets the request fail produce the same single
 * message rather than two.
 */
export function refusedWhileViewing(): boolean {
  if (!isViewingAsCustomer()) return false;
  // Imported lazily: this module is loaded before the app, and pulling the
  // toast library in at that point would drag React in with it.
  import("react-toastify")
    .then(({ toast }) =>
      toast.info("Not available while viewing", {
        toastId: "view_only",
        autoClose: 1200,
        closeButton: false,
      }),
    )
    .catch(() => undefined);
  return true;
}
